import {
  IInvoiceDetail,
  IInvoiceLineItem,
  IInvoiceListItem,
  IInvoiceRaw,
  IInvoiceRawDetail,
  IInvoiceRawLineItem,
} from "./invoices.api.types";
import {
  EHotelServiceRequestType,
  normalizeInvoiceStatusCode,
} from "../../constants";
import { EServiceRequestType } from "../../types/global.types";

const REVIEW_STATE_LABELS: Record<string, string> = {
  NEW: "New",
  PENDING_VENDOR_REVIEW: "Pending",
  SUBMITTED_TO_IMS: "Submitted to IMS",
  ACCEPTED: "Accepted",
  REJECTED_PENDING_ADMIN_REVIEW: "Rejected",
  REJECTED_NEW_INVOICE_GENERATED: "Rejected · New Invoice Generated",
  REJECTED_MANUAL_INTERVENTION: "Rejected · Needs Manual Intervention",
  PAID: "Paid",
  APPROVED: "Accepted",
  SUBMITTED: "Submitted to IMS",
  REJECTED: "Rejected",
  SUPERSEDED: "Rejected · New Invoice Generated",
};

const ALL_LINES_APPROVED_STATES = ["submitted to ims", "accepted", "paid"];
export const areAllLinesApproved = (status?: string): boolean =>
  ALL_LINES_APPROVED_STATES.includes((status || "").trim().toLowerCase());

const PAYMENT_FOR_REVIEW_STATE: Record<string, string> = {
  NEW: "",
  PENDING_VENDOR_REVIEW: "Pending Approval",
  SUBMITTED_TO_IMS: "Pending Approval",
  ACCEPTED: "Pending Payment",
  REJECTED_PENDING_ADMIN_REVIEW: "Rejected",
  REJECTED_NEW_INVOICE_GENERATED: "Rejected",
  REJECTED_MANUAL_INTERVENTION: "Rejected",
  PAID: "Paid",
  APPROVED: "Pending Payment",
  SUBMITTED: "Pending Approval",
  REJECTED: "Rejected",
  SUPERSEDED: "Rejected",
};

const decode = (table: Record<string, string>, code: unknown): string => {
  const key = String(code ?? "");
  return table[key] ?? key;
};

const startOfDay = (value: unknown): string | undefined => {
  const day = String(value ?? "").split("T")[0];
  return day ? `${day}T00:00:00.000Z` : undefined;
};
const endOfDay = (value: unknown): string | undefined => {
  const day = String(value ?? "").split("T")[0];
  return day ? `${day}T23:59:59.999Z` : undefined;
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const selectItems = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  const payload = res?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

export const selectInvoiceList = (res: any): IInvoiceRaw[] => selectItems(res);

export const selectInvoiceLineItems = (res: any): IInvoiceRawLineItem[] =>
  selectItems(res);

const MEAL_TYPES: string[] = Object.values(EHotelServiceRequestType);

export const mapInvoiceListItem = (raw: IInvoiceRaw): IInvoiceListItem => {
  const status = normalizeInvoiceStatusCode(raw.invoice_status);
  return {
    id: String(raw.invoice_id ?? ""),
    invoiceNumber: String(raw.invoice_number ?? raw.invoice_id ?? ""),
    amountDue: toNumber(raw.invoice_amt),
    status,
    paymentStatus: PAYMENT_FOR_REVIEW_STATE[status] ?? "",
    vendorName: String(raw.vendorname ?? ""),
    vendorId: String(raw.vendorid ?? ""),
    createdOn: String(raw.created_date ?? ""),
    periodStart: startOfDay(raw.service_period_from ?? raw.invoice_start_dt),
    periodEnd: endOfDay(raw.service_period_to ?? raw.invoice_end_dt),
    updatedOn: String(raw.updated_date || raw.created_date || ""),
    vendorRejectionReason:
      raw.rejection_reason_desc ??
      raw.rejection_reason_cd ??
      raw.vendor_rejection_reason ??
      undefined,
    rejectionReasonCd: raw.rejection_reason_cd ?? undefined,
    rejectionComments: raw.rejection_comments ?? undefined,
    vendorCode: raw.vendor_cd ?? undefined,
    vendorType: raw.vendor_type ?? undefined,
  };
};

export const mapLineItem = (raw: IInvoiceRawLineItem): IInvoiceLineItem => {
  const rawType = String(raw.requesttype ?? "");
  const isMeal = MEAL_TYPES.includes(rawType.toUpperCase());
  const amount = raw.cost ?? raw.line_amount;

  const compid = raw.requesttype === EServiceRequestType.HOTEL ? raw.companion_crew_id || undefined : undefined;

  return {
    id: String(raw.line_id ?? raw.invoice_detail_id ?? ""),
    tmId: String(raw.crewid ?? raw.crewno ?? ""),
    crewName: raw.crew_name || undefined,
    movementId:
      raw.movementid ?? (raw.movement_id != null ? String(raw.movement_id) : undefined),
    movementDate: raw.movementdate ?? raw.moementdate ?? raw.movement_dt ?? undefined,
    requestType: isMeal ? EServiceRequestType.MEAL_SCAN : rawType,
    scannedOn: String(raw.scannedon ?? raw.scan_datetime ?? ""),
    vendorId: String(raw.vendorid ?? ""),
    createdOn: String(raw.created_date ?? ""),
    updatedOn: String(raw.updated_date ?? ""),
    arrivalDate: raw.arrival_date,
    greeterName: raw.scannedby ?? undefined,
    itemCost: amount == null ? undefined : toNumber(amount),
    inputType: raw.input_type,
    lineStatus: raw.status,
    rejectionReason: raw.rejection_reason,
    rejectionComments: raw.rejection_comments,
    isNewlyAdded: !!raw.is_newly_added,
    reviewedOn: raw.reviewed_on ?? undefined,
    mealType: isMeal ? rawType.toUpperCase() : raw.meal_type ?? undefined,
    isManualScan: String(raw.is_manual_scan ?? "").trim().toUpperCase() === "Y",
    companionCrewId: compid,
  };
};

export const mapInvoiceDetail = (raw: any): IInvoiceDetail | undefined => {
  if (!raw) return undefined;
  const detail = raw as IInvoiceRawDetail;

  return {
    id: String(detail.invoice_id ?? ""),
    invoiceNumber: String(detail.invoice_number ?? detail.invoice_id ?? ""),
    status: decode(REVIEW_STATE_LABELS, detail.invoice_status),
    paymentStatus:
      PAYMENT_FOR_REVIEW_STATE[String(detail.invoice_status ?? "")] ?? "",
    totalAmount: toNumber(detail.invoice_amt).toFixed(2),
    vendorName: String(detail.vendorname ?? ""),
    vendorId: String(detail.vendorid ?? ""),
    createdOn: String(detail.created_date ?? ""),
    updatedOn: String(detail.updated_date || detail.created_date || ""),
    periodStart: startOfDay(detail.service_period_from ?? detail.invoice_start_dt),
    periodEnd: endOfDay(detail.service_period_to ?? detail.invoice_end_dt),
    version: Math.max(0, toNumber(detail.invoice_version) - 1),
    clpReviewNotes: detail.clp_review_notes,
    vendorRejectionReason:
      detail.rejection_reason_desc ??
      detail.rejection_reason_cd ??
      detail.vendor_rejection_reason,
    rejectionReason: detail.rejection_reason,
    vendorEmail: detail.vendor_email,
    vendorType: detail.vendor_type ?? undefined,
    uniqueVendorId: detail.unique_vendor_id ?? null,
    companyProfileNumber: detail.company_profile_number,
    vendorComments: detail.vendor_comments,
    vendorInvoiceId: detail.vendor_invoice_no ?? undefined,
    lineItems: (detail.lineItems ?? []).map(mapLineItem),
  };
};
